import { Badge, Group, Stack, Text } from "@mantine/core";
import { type FragmentOf, graphql, readFragment } from "gql.tada";

/**
 * Outcome of a manual override, as documented by the schema docstring on
 * `RuleEngineManualOverridePayload.effect` (currently typed as `String!`
 * upstream — track replacing it with a GraphQL enum). The mapping here
 * is the single boundary where the raw string is narrowed.
 */
enum ManualOverrideEffect {
  Fired = "fired",
  Skipped = "skipped",
  Replayed = "replayed",
}

const EFFECT_COLORS: Record<ManualOverrideEffect, string> = {
  [ManualOverrideEffect.Fired]: "green",
  [ManualOverrideEffect.Skipped]: "gray",
  [ManualOverrideEffect.Replayed]: "blue",
};

const FALLBACK_COLOR = "yellow";

function isManualOverrideEffect(value: string): value is ManualOverrideEffect {
  return (Object.values(ManualOverrideEffect) as string[]).includes(value);
}

export const RuleEngineManualOverrideFragment = graphql(`
  fragment RuleEngineManualOverrideFields on RuleEngineManualOverridePayload {
    __typename
    ruleId
    overriddenBy
    reason
    effect
  }
`);

interface Props {
  data: FragmentOf<typeof RuleEngineManualOverrideFragment>;
}

export function RuleEngineManualOverridePayload({ data }: Props) {
  const p = readFragment(RuleEngineManualOverrideFragment, data);
  const color = isManualOverrideEffect(p.effect)
    ? EFFECT_COLORS[p.effect]
    : FALLBACK_COLOR;
  return (
    <Stack gap={4}>
      <Group gap="xs" wrap="wrap">
        <Badge color={color} size="xs">
          MANUAL OVERRIDE · {p.effect.toUpperCase()}
        </Badge>
        <Text size="xs">
          rule {p.ruleId.slice(0, 8)} by account {p.overriddenBy.slice(0, 8)}
        </Text>
      </Group>
      <Text size="xs" c="dimmed">
        {p.reason}
      </Text>
    </Stack>
  );
}
